import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Wrench, Download, Loader2, Printer, FileText, FileDown,
  LayoutList, Calculator, Award, Shapes, BookText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import SpEdKitLogo from '../components/SpEdKitLogo';
import { toast } from 'sonner';

// Grade level bands with complexity indicators
const GRADE_LEVELS = [
  { id: 'ece', name: 'Early Childhood (ECE)', description: 'Pre-K to Kindergarten', grades: 'Pre-K - K', complexity: 1 },
  { id: 'early-elementary', name: 'Early Elementary', description: 'Grades 1-2', grades: '1st - 2nd', complexity: 2 },
  { id: 'upper-elementary', name: 'Upper Elementary', description: 'Grades 3-5', grades: '3rd - 5th', complexity: 3 },
  { id: 'secondary', name: 'Secondary', description: 'Grades 6-12', grades: '6th - 12th', complexity: 4 },
];

// Available tools by category
const TOOL_CATEGORIES = {
  math: {
    name: 'Math Tools',
    icon: Calculator,
    tools: [
      { id: 'hundreds-chart', name: '100s Chart', description: 'Number grid 1-100', levels: ['early-elementary', 'upper-elementary'] },
      { id: 'number-line', name: 'Number Line', description: 'Adjusts range by grade', levels: ['ece', 'early-elementary', 'upper-elementary', 'secondary'] },
      { id: 'multiplication-chart', name: 'Multiplication Chart', description: '12x12 multiplication table', levels: ['upper-elementary', 'secondary'] },
      { id: 'place-value-chart', name: 'Place Value Chart', description: 'Adjusts columns by grade', levels: ['early-elementary', 'upper-elementary', 'secondary'] },
      { id: 'fraction-strips', name: 'Fraction Strips', description: 'Visual fraction reference', levels: ['upper-elementary', 'secondary'] },
    ]
  },
  graphic: {
    name: 'Graphic Organizers',
    icon: Shapes,
    tools: [
      { id: 'venn-diagram', name: 'Venn Diagram', description: 'Compare and contrast', levels: ['early-elementary', 'upper-elementary', 'secondary'] },
      { id: 'kwl-chart', name: 'KWL Chart', description: 'Know, Want to Know, Learned', levels: ['early-elementary', 'upper-elementary', 'secondary'] },
      { id: 'story-map', name: 'Story Map', description: 'Complexity adjusts by grade', levels: ['ece', 'early-elementary', 'upper-elementary', 'secondary'] },
      { id: 'cause-effect', name: 'Cause & Effect', description: 'Show relationships', levels: ['upper-elementary', 'secondary'] },
      { id: 'main-idea', name: 'Main Idea & Details', description: 'Identify key concepts', levels: ['early-elementary', 'upper-elementary', 'secondary'] },
      { id: 'sequence-chart', name: 'Sequence Chart', description: 'Order of events', levels: ['ece', 'early-elementary', 'upper-elementary'] },
    ]
  },
  behavior: {
    name: 'Behavior Supports',
    icon: Award,
    tools: [
      { id: 'first-then', name: 'First/Then Board', description: 'Visual schedule for tasks', levels: ['ece', 'early-elementary', 'upper-elementary', 'secondary'] },
      { id: 'token-board', name: 'Token Board', description: 'Token count adjusts by grade', levels: ['ece', 'early-elementary', 'upper-elementary'] },
      { id: 'choice-board', name: 'Choice Board', description: 'Activity choices', levels: ['ece', 'early-elementary', 'upper-elementary'] },
      { id: 'visual-schedule', name: 'Visual Schedule', description: 'Daily routine cards', levels: ['ece', 'early-elementary', 'upper-elementary'] },
      { id: 'self-monitoring', name: 'Self-Monitoring Checklist', description: 'Complexity adjusts by grade', levels: ['upper-elementary', 'secondary'] },
      { id: 'break-cards', name: 'Break Cards', description: 'Request break visuals', levels: ['ece', 'early-elementary', 'upper-elementary'] },
    ]
  },
  writing: {
    name: 'Writing Supports',
    icon: BookText,
    tools: [
      { id: 'lined-paper', name: 'Lined Paper', description: 'Line spacing adjusts by grade', levels: ['ece', 'early-elementary', 'upper-elementary', 'secondary'] },
      { id: 'paragraph-frame', name: 'Paragraph Frame', description: 'Sentence starters', levels: ['early-elementary', 'upper-elementary', 'secondary'] },
      { id: 'essay-outline', name: 'Essay Outline', description: 'Structure adjusts by grade', levels: ['upper-elementary', 'secondary'] },
      { id: 'word-bank-template', name: 'Word Bank Template', description: 'Vocabulary support', levels: ['ece', 'early-elementary', 'upper-elementary'] },
      { id: 'editing-checklist', name: 'Editing Checklist', description: 'Proofreading guide', levels: ['upper-elementary', 'secondary'] },
    ]
  },
  organization: {
    name: 'Organization Tools',
    icon: LayoutList,
    tools: [
      { id: 'task-checklist', name: 'Task Checklist', description: 'Step-by-step tracker', levels: ['ece', 'early-elementary', 'upper-elementary', 'secondary'] },
      { id: 'homework-planner', name: 'Homework Planner', description: 'Weekly assignment tracker', levels: ['upper-elementary', 'secondary'] },
      { id: 'timer-visual', name: 'Visual Timer Card', description: 'Time remaining visual', levels: ['ece', 'early-elementary', 'upper-elementary'] },
      { id: 'transition-cards', name: 'Transition Cards', description: 'Activity change visuals', levels: ['ece', 'early-elementary'] },
      { id: 'goal-tracker', name: 'Goal Tracker Sheet', description: 'Progress monitoring', levels: ['upper-elementary', 'secondary'] },
    ]
  },
};

// Get complexity level from grade ID
const getComplexity = (gradeLevel) => {
  const grade = GRADE_LEVELS.find(g => g.id === gradeLevel);
  return grade ? grade.complexity : 2; // Default to early-elementary
};

// Tool generation templates that adapt to grade level
const generateToolHTML = (toolId, gradeLevel) => {
  const complexity = getComplexity(gradeLevel);
  
  const toolGenerators = {
    'hundreds-chart': () => `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Hundreds Chart</h2>
        <table style="border-collapse: collapse; width: 100%; max-width: 600px; margin: 0 auto;">
          ${Array.from({length: 10}, (_, row) => `
            <tr>
              ${Array.from({length: 10}, (_, col) => {
                const num = row * 10 + col + 1;
                return `<td style="border: 1px solid #333; padding: ${complexity >= 3 ? '10px' : '14px'}; text-align: center; font-size: ${complexity >= 3 ? '14px' : '18px'}; font-weight: 500; width: 10%;">${num}</td>`;
              }).join('')}
            </tr>
          `).join('')}
        </table>
        <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
      </div>
    `,
    
    'number-line': () => {
      // Adjust number line based on grade level
      let start, end, step, title;
      if (complexity === 1) { // ECE
        start = 0; end = 10; step = 1; title = 'Number Line (0-10)';
      } else if (complexity === 2) { // Early Elementary
        start = 0; end = 20; step = 1; title = 'Number Line (0-20)';
      } else if (complexity === 3) { // Upper Elementary
        start = 0; end = 100; step = 10; title = 'Number Line (0-100)';
      } else { // Secondary - includes negative numbers
        start = -20; end = 20; step = 5; title = 'Number Line (-20 to 20)';
      }
      
      const count = (end - start) / step + 1;
      const spacing = 640 / (count - 1);
      
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 30px;">${title}</h2>
          <div style="max-width: 700px; margin: 0 auto;">
            <svg viewBox="0 0 700 80" style="width: 100%;">
              <line x1="30" y1="40" x2="670" y2="40" stroke="#333" stroke-width="3"/>
              ${Array.from({length: count}, (_, i) => {
                const num = start + (i * step);
                return `
                  <line x1="${30 + i * spacing}" y1="30" x2="${30 + i * spacing}" y2="50" stroke="#333" stroke-width="2"/>
                  <text x="${30 + i * spacing}" y="70" text-anchor="middle" font-size="14" font-weight="500">${num}</text>
                `;
              }).join('')}
            </svg>
          </div>
          <div style="max-width: 700px; margin: 40px auto 0;">
            <svg viewBox="0 0 700 80" style="width: 100%;">
              <line x1="30" y1="40" x2="670" y2="40" stroke="#333" stroke-width="3"/>
              ${Array.from({length: count}, (_, i) => `
                <line x1="${30 + i * spacing}" y1="30" x2="${30 + i * spacing}" y2="50" stroke="#333" stroke-width="2"/>
              `).join('')}
            </svg>
          </div>
          <p style="text-align: center; margin-top: 30px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'multiplication-chart': () => {
      const size = complexity >= 4 ? 12 : 10; // Secondary gets full 12x12
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Multiplication Chart (${size}x${size})</h2>
          <table style="border-collapse: collapse; width: 100%; max-width: 650px; margin: 0 auto;">
            <tr>
              <td style="border: 2px solid #4A7C59; padding: 6px; text-align: center; font-weight: bold; background: #4A7C59; color: white;">×</td>
              ${Array.from({length: size}, (_, i) => `
                <td style="border: 2px solid #4A7C59; padding: 6px; text-align: center; font-weight: bold; background: #4A7C59; color: white;">${i + 1}</td>
              `).join('')}
            </tr>
            ${Array.from({length: size}, (_, row) => `
              <tr>
                <td style="border: 2px solid #4A7C59; padding: 6px; text-align: center; font-weight: bold; background: #4A7C59; color: white;">${row + 1}</td>
                ${Array.from({length: size}, (_, col) => `
                  <td style="border: 1px solid #ccc; padding: 6px; text-align: center; font-size: 12px;">${(row + 1) * (col + 1)}</td>
                `).join('')}
              </tr>
            `).join('')}
          </table>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'place-value-chart': () => {
      // Adjust columns based on grade level
      let columns;
      if (complexity <= 2) {
        columns = ['Hundreds', 'Tens', 'Ones'];
      } else if (complexity === 3) {
        columns = ['Thousands', 'Hundreds', 'Tens', 'Ones'];
      } else {
        columns = ['Millions', 'Hundred Thousands', 'Ten Thousands', 'Thousands', 'Hundreds', 'Tens', 'Ones'];
      }
      const colors = ['#4A7C59', '#6366F1', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4'];
      
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Place Value Chart</h2>
          <table style="border-collapse: collapse; width: 100%; max-width: ${columns.length * 100}px; margin: 0 auto;">
            <tr>
              ${columns.map((col, i) => `
                <td style="border: 2px solid ${colors[i % colors.length]}; padding: 12px; text-align: center; font-weight: bold; background: ${colors[i % colors.length]}; color: white; font-size: ${columns.length > 5 ? '10px' : '14px'};">${col}</td>
              `).join('')}
            </tr>
            ${Array.from({length: 6}, () => `
              <tr>
                ${columns.map((_, i) => `
                  <td style="border: 2px solid ${colors[i % colors.length]}; padding: 20px; text-align: center;"></td>
                `).join('')}
              </tr>
            `).join('')}
          </table>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'fraction-strips': () => {
      const fractions = complexity >= 4 
        ? [1, 2, 3, 4, 5, 6, 8, 10, 12] // Secondary includes more complex fractions
        : [1, 2, 3, 4, 5, 6, 8];
      const colors = ['#4A7C59', '#6366F1', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444', '#84CC16'];
      
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Fraction Strips</h2>
          <div style="max-width: 600px; margin: 0 auto;">
            ${fractions.map((denom, idx) => `
              <div style="display: flex; margin-bottom: 4px; height: 30px;">
                ${Array.from({length: denom}, (_, i) => `
                  <div style="flex: 1; background: ${colors[idx % colors.length]}; border: 1px solid #333; display: flex; align-items: center; justify-content: center; color: white; font-size: ${denom > 6 ? '10px' : '12px'}; font-weight: bold;">
                    ${denom === 1 ? '1' : `1/${denom}`}
                  </div>
                `).join('')}
              </div>
            `).join('')}
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'venn-diagram': () => `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align: center; color: #4A7C59; margin-bottom: 10px;">Venn Diagram</h2>
        <p style="text-align: center; color: #666; margin-bottom: 20px;">Compare and Contrast</p>
        <div style="max-width: 600px; margin: 0 auto; position: relative; height: 350px;">
          <div style="position: absolute; left: 50px; top: 50px; width: 250px; height: 250px; border: 3px solid #6366F1; border-radius: 50%; background: rgba(99, 102, 241, 0.1);"></div>
          <div style="position: absolute; right: 50px; top: 50px; width: 250px; height: 250px; border: 3px solid #10B981; border-radius: 50%; background: rgba(16, 185, 129, 0.1);"></div>
          <div style="position: absolute; left: 100px; top: 0; text-align: center; width: 150px;">
            <div style="border-bottom: 2px solid #6366F1; padding-bottom: 5px; color: #6366F1; font-weight: bold;">Topic 1</div>
          </div>
          <div style="position: absolute; right: 100px; top: 0; text-align: center; width: 150px;">
            <div style="border-bottom: 2px solid #10B981; padding-bottom: 5px; color: #10B981; font-weight: bold;">Topic 2</div>
          </div>
          <div style="position: absolute; left: 50%; top: 320px; transform: translateX(-50%); text-align: center;">
            <span style="color: #666; font-size: 14px;">Both</span>
          </div>
        </div>
        <p style="text-align: center; margin-top: 30px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
      </div>
    `,
    
    'kwl-chart': () => `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align: center; color: #4A7C59; margin-bottom: 10px;">KWL Chart</h2>
        <p style="text-align: center; margin-bottom: 20px;">Topic: <span style="border-bottom: 2px solid #333; padding: 0 80px;"></span></p>
        <table style="border-collapse: collapse; width: 100%; max-width: 700px; margin: 0 auto;">
          <tr>
            <td style="border: 3px solid #6366F1; padding: 15px; text-align: center; font-weight: bold; background: #6366F1; color: white; width: 33%;">K - What I KNOW</td>
            <td style="border: 3px solid #F59E0B; padding: 15px; text-align: center; font-weight: bold; background: #F59E0B; color: white; width: 33%;">W - What I WANT to Know</td>
            <td style="border: 3px solid #10B981; padding: 15px; text-align: center; font-weight: bold; background: #10B981; color: white; width: 33%;">L - What I LEARNED</td>
          </tr>
          <tr>
            <td style="border: 2px solid #6366F1; padding: 15px; vertical-align: top; height: 300px;"></td>
            <td style="border: 2px solid #F59E0B; padding: 15px; vertical-align: top; height: 300px;"></td>
            <td style="border: 2px solid #10B981; padding: 15px; vertical-align: top; height: 300px;"></td>
          </tr>
        </table>
        <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
      </div>
    `,
    
    'story-map': () => {
      // Simpler for younger grades
      if (complexity <= 2) {
        return `
          <div style="font-family: Arial, sans-serif; padding: 20px;">
            <h2 style="text-align: center; color: #4A7C59; margin-bottom: 10px;">Story Map</h2>
            <p style="text-align: center; margin-bottom: 20px;">Title: <span style="border-bottom: 2px solid #333; padding: 0 80px;"></span></p>
            <div style="max-width: 600px; margin: 0 auto;">
              <div style="border: 3px solid #F59E0B; border-radius: 16px; padding: 20px; margin-bottom: 20px;">
                <p style="font-weight: bold; color: #F59E0B; margin-bottom: 10px; font-size: 20px;">Beginning</p>
                <div style="min-height: 100px; border: 2px dashed #ccc; border-radius: 8px;"></div>
              </div>
              <div style="border: 3px solid #EC4899; border-radius: 16px; padding: 20px; margin-bottom: 20px;">
                <p style="font-weight: bold; color: #EC4899; margin-bottom: 10px; font-size: 20px;">Middle</p>
                <div style="min-height: 100px; border: 2px dashed #ccc; border-radius: 8px;"></div>
              </div>
              <div style="border: 3px solid #8B5CF6; border-radius: 16px; padding: 20px;">
                <p style="font-weight: bold; color: #8B5CF6; margin-bottom: 10px; font-size: 20px;">End</p>
                <div style="min-height: 100px; border: 2px dashed #ccc; border-radius: 8px;"></div>
              </div>
            </div>
            <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
          </div>
        `;
      }
      // More complex for older grades
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 10px;">Story Map</h2>
          <p style="text-align: center; margin-bottom: 20px;">Title: <span style="border-bottom: 2px solid #333; padding: 0 80px;"></span></p>
          <div style="max-width: 600px; margin: 0 auto;">
            <div style="display: flex; gap: 20px; margin-bottom: 20px;">
              <div style="flex: 1; border: 2px solid #6366F1; border-radius: 12px; padding: 15px;">
                <p style="font-weight: bold; color: #6366F1; margin-bottom: 10px;">Characters:</p>
                <div style="min-height: 60px;"></div>
              </div>
              <div style="flex: 1; border: 2px solid #10B981; border-radius: 12px; padding: 15px;">
                <p style="font-weight: bold; color: #10B981; margin-bottom: 10px;">Setting:</p>
                <div style="min-height: 60px;"></div>
              </div>
            </div>
            <div style="border: 2px solid #F59E0B; border-radius: 12px; padding: 15px; margin-bottom: 15px;">
              <p style="font-weight: bold; color: #F59E0B; margin-bottom: 10px;">Problem/Conflict:</p>
              <div style="min-height: 60px;"></div>
            </div>
            <div style="border: 2px solid #EC4899; border-radius: 12px; padding: 15px; margin-bottom: 15px;">
              <p style="font-weight: bold; color: #EC4899; margin-bottom: 10px;">Rising Action (Key Events):</p>
              <div style="min-height: 80px;"></div>
            </div>
            <div style="border: 2px solid #EF4444; border-radius: 12px; padding: 15px; margin-bottom: 15px;">
              <p style="font-weight: bold; color: #EF4444; margin-bottom: 10px;">Climax:</p>
              <div style="min-height: 60px;"></div>
            </div>
            <div style="border: 2px solid #8B5CF6; border-radius: 12px; padding: 15px;">
              <p style="font-weight: bold; color: #8B5CF6; margin-bottom: 10px;">Resolution:</p>
              <div style="min-height: 60px;"></div>
            </div>
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'cause-effect': () => {
      const pairs = complexity >= 4 ? 4 : 3;
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Cause & Effect</h2>
          <p style="text-align: center; margin-bottom: 20px;">Topic: <span style="border-bottom: 2px solid #333; padding: 0 80px;"></span></p>
          <div style="max-width: 650px; margin: 0 auto;">
            ${Array.from({length: pairs}, (_, i) => `
              <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 20px;">
                <div style="flex: 1; border: 3px solid #6366F1; border-radius: 12px; padding: 15px; min-height: 60px;">
                  <p style="font-weight: bold; color: #6366F1; margin-bottom: 8px; font-size: 14px;">CAUSE ${i + 1}</p>
                </div>
                <div style="font-size: 30px; color: #4A7C59;">→</div>
                <div style="flex: 1; border: 3px solid #10B981; border-radius: 12px; padding: 15px; min-height: 60px;">
                  <p style="font-weight: bold; color: #10B981; margin-bottom: 8px; font-size: 14px;">EFFECT ${i + 1}</p>
                </div>
              </div>
            `).join('')}
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'main-idea': () => {
      const details = complexity >= 3 ? 4 : 3;
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Main Idea & Details</h2>
          <div style="max-width: 600px; margin: 0 auto;">
            <div style="border: 4px solid #6366F1; border-radius: 16px; padding: 20px; text-align: center; margin-bottom: 20px; background: #EEF2FF;">
              <p style="font-weight: bold; color: #6366F1; margin-bottom: 10px; font-size: 18px;">MAIN IDEA</p>
              <div style="min-height: 60px; border: 2px dashed #6366F1; border-radius: 8px; background: white;"></div>
            </div>
            <div style="display: flex; justify-content: center; margin-bottom: 20px;">
              <div style="width: 4px; height: 30px; background: #6366F1;"></div>
            </div>
            <div style="display: grid; grid-template-columns: repeat(${details > 3 ? 2 : details}, 1fr); gap: 15px;">
              ${Array.from({length: details}, (_, i) => `
                <div style="border: 3px solid #10B981; border-radius: 12px; padding: 15px; text-align: center; background: #ECFDF5;">
                  <p style="font-weight: bold; color: #10B981; margin-bottom: 10px;">Detail ${i + 1}</p>
                  <div style="min-height: 70px; border: 2px dashed #10B981; border-radius: 8px; background: white;"></div>
                </div>
              `).join('')}
            </div>
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'sequence-chart': () => {
      const steps = complexity <= 1 ? 3 : complexity <= 2 ? 4 : 5;
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Sequence Chart</h2>
          <p style="text-align: center; margin-bottom: 20px;">Topic: <span style="border-bottom: 2px solid #333; padding: 0 80px;"></span></p>
          <div style="max-width: 550px; margin: 0 auto;">
            ${Array.from({length: steps}, (_, i) => {
              const labels = ['First', 'Next', 'Then', 'After That', 'Finally'];
              return `
                <div style="display: flex; align-items: center; gap: 15px; margin-bottom: ${i < steps - 1 ? '10px' : '0'};">
                  <div style="width: 80px; text-align: center; font-weight: bold; color: #6366F1; font-size: 14px;">${labels[i] || `Step ${i + 1}`}</div>
                  <div style="flex: 1; border: 3px solid #6366F1; border-radius: 12px; padding: 15px; min-height: 50px;"></div>
                </div>
                ${i < steps - 1 ? '<div style="width: 80px; text-align: center;"><span style="font-size: 24px; color: #6366F1;">↓</span></div>' : ''}
              `;
            }).join('')}
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'first-then': () => `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">First / Then Board</h2>
        <div style="display: flex; gap: 20px; max-width: 600px; margin: 0 auto; justify-content: center;">
          <div style="flex: 1; border: 4px solid #6366F1; border-radius: 16px; padding: 20px; text-align: center;">
            <h3 style="color: #6366F1; font-size: 28px; margin-bottom: 20px;">FIRST</h3>
            <div style="border: 2px dashed #ccc; border-radius: 12px; height: 180px; display: flex; align-items: center; justify-content: center; background: #f9fafb;">
              <span style="color: #9ca3af; font-size: 14px;">(Place image or write task here)</span>
            </div>
          </div>
          <div style="display: flex; align-items: center;">
            <span style="font-size: 48px; color: #4A7C59;">→</span>
          </div>
          <div style="flex: 1; border: 4px solid #10B981; border-radius: 16px; padding: 20px; text-align: center;">
            <h3 style="color: #10B981; font-size: 28px; margin-bottom: 20px;">THEN</h3>
            <div style="border: 2px dashed #ccc; border-radius: 12px; height: 180px; display: flex; align-items: center; justify-content: center; background: #f9fafb;">
              <span style="color: #9ca3af; font-size: 14px;">(Place image or write reward here)</span>
            </div>
          </div>
        </div>
        <p style="text-align: center; margin-top: 30px; font-size: 12px; color: #666;">Student: _________________ Date: _________________</p>
      </div>
    `,
    
    'token-board': () => {
      const tokens = complexity <= 1 ? 5 : complexity <= 2 ? 8 : 10;
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Token Board (${tokens} Tokens)</h2>
          <div style="max-width: 550px; margin: 0 auto; border: 3px solid #4A7C59; border-radius: 16px; padding: 20px;">
            <p style="text-align: center; font-size: 18px; margin-bottom: 20px;">I am working for: <span style="border-bottom: 2px solid #333; padding: 0 40px;"></span></p>
            <div style="display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; margin-bottom: 20px;">
              ${Array.from({length: tokens}, (_, i) => `
                <div style="width: ${tokens > 8 ? '50px' : '60px'}; height: ${tokens > 8 ? '50px' : '60px'}; border: 3px solid #6366F1; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: ${tokens > 8 ? '18px' : '22px'}; font-weight: bold; color: #6366F1; background: white;">
                  ${i + 1}
                </div>
              `).join('')}
            </div>
            <div style="border: 2px dashed #10B981; border-radius: 12px; padding: 15px; text-align: center; background: #ecfdf5;">
              <p style="color: #10B981; font-weight: bold; margin: 0;">🎉 REWARD 🎉</p>
              <div style="margin-top: 10px; border-bottom: 2px solid #10B981; padding-bottom: 5px;"></div>
            </div>
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Student: _________________</p>
        </div>
      `;
    },
    
    'choice-board': () => {
      const choices = complexity <= 1 ? 4 : 6;
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Choice Board</h2>
          <p style="text-align: center; margin-bottom: 20px; font-size: 18px;">Pick an activity:</p>
          <div style="display: grid; grid-template-columns: repeat(${choices > 4 ? 3 : 2}, 1fr); gap: 15px; max-width: 500px; margin: 0 auto;">
            ${Array.from({length: choices}, (_, i) => `
              <div style="border: 3px solid #6366F1; border-radius: 12px; padding: 20px; text-align: center; min-height: 100px; display: flex; align-items: center; justify-content: center; background: white;">
                <span style="color: #9ca3af; font-size: 12px;">Choice ${i + 1}</span>
              </div>
            `).join('')}
          </div>
          <p style="text-align: center; margin-top: 30px; font-size: 12px; color: #666;">Student: _________________</p>
        </div>
      `;
    },
    
    'visual-schedule': () => {
      const slots = complexity <= 1 ? 5 : 7;
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">My Daily Schedule</h2>
          <div style="max-width: 400px; margin: 0 auto;">
            ${Array.from({length: slots}, (_, i) => `
              <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 15px;">
                <div style="width: 40px; height: 40px; border: 2px solid #6366F1; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: bold; color: #6366F1;">${i + 1}</div>
                <div style="flex: 1; border: 2px solid #ccc; border-radius: 12px; padding: 15px; min-height: 50px; display: flex; align-items: center;">
                  <span style="color: #9ca3af; font-size: 12px;">Activity ${i + 1}</span>
                </div>
                <div style="width: 30px; height: 30px; border: 2px solid #10B981; border-radius: 6px;"></div>
              </div>
            `).join('')}
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 11px; color: #666;">Check the box ☑ when each activity is done!</p>
          <p style="text-align: center; margin-top: 10px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'self-monitoring': () => {
      const behaviors = complexity >= 4 
        ? ['I came to class prepared', 'I stayed focused on my work', 'I participated appropriately', 'I completed assigned tasks', 'I managed my time effectively', 'I used appropriate problem-solving', 'I asked for help when needed', 'I showed respect to others']
        : ['I came to class prepared', 'I stayed in my seat', 'I raised my hand to speak', 'I completed my work', 'I was respectful to others', 'I followed directions'];
      
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 10px;">Self-Monitoring Checklist</h2>
          <p style="text-align: center; margin-bottom: 20px;">Date: _____________ Class/Period: _____________</p>
          <table style="border-collapse: collapse; width: 100%; max-width: 600px; margin: 0 auto;">
            <tr style="background: #4A7C59; color: white;">
              <th style="border: 2px solid #4A7C59; padding: 12px; text-align: left;">Behavior</th>
              <th style="border: 2px solid #4A7C59; padding: 12px; text-align: center; width: 60px;">Yes</th>
              <th style="border: 2px solid #4A7C59; padding: 12px; text-align: center; width: 60px;">No</th>
            </tr>
            ${behaviors.map(item => `
              <tr>
                <td style="border: 1px solid #ccc; padding: 12px;">${item}</td>
                <td style="border: 1px solid #ccc; padding: 12px; text-align: center;">☐</td>
                <td style="border: 1px solid #ccc; padding: 12px; text-align: center;">☐</td>
              </tr>
            `).join('')}
          </table>
          <div style="max-width: 600px; margin: 20px auto 0;">
            <p style="font-weight: bold; margin-bottom: 10px;">Reflection: What will I do differently next time?</p>
            <div style="border: 2px solid #ccc; border-radius: 8px; min-height: 80px; padding: 10px;"></div>
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Student Signature: _________________</p>
        </div>
      `;
    },
    
    'break-cards': () => `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Break Cards</h2>
        <p style="text-align: center; color: #666; margin-bottom: 20px;">Cut along dotted lines. Give to student to request breaks.</p>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; max-width: 500px; margin: 0 auto;">
          ${['I need a break', 'I need help', 'I need space', 'I need to move'].map((text, i) => `
            <div style="border: 3px dashed #6366F1; border-radius: 16px; padding: 25px; text-align: center; background: ${i % 2 === 0 ? '#EEF2FF' : '#ECFDF5'};">
              <div style="font-size: 40px; margin-bottom: 10px;">${['🖐️', '❓', '🧘', '🏃'][i]}</div>
              <p style="font-weight: bold; color: #333; font-size: 16px;">${text}</p>
            </div>
          `).join('')}
        </div>
        <p style="text-align: center; margin-top: 30px; font-size: 12px; color: #666;">Student: _________________</p>
      </div>
    `,
    
    'lined-paper': () => {
      const lineHeight = complexity <= 1 ? 40 : complexity <= 2 ? 32 : complexity <= 3 ? 24 : 20;
      const lines = Math.floor(400 / lineHeight);
      const title = complexity <= 1 ? 'Extra-Wide Ruled Paper' : complexity <= 2 ? 'Wide-Ruled Paper' : complexity <= 3 ? 'Medium-Ruled Paper' : 'College-Ruled Paper';
      
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">${title}</h2>
          <div style="max-width: 600px; margin: 0 auto; border: 1px solid #ccc; padding: 20px;">
            <p style="margin-bottom: 15px;">Name: _________________ Date: _________________</p>
            <div style="margin-top: 10px;">
              ${Array.from({length: lines}, () => `
                <div style="border-bottom: 1px solid #ccc; height: ${lineHeight}px;"></div>
              `).join('')}
            </div>
          </div>
        </div>
      `;
    },
    
    'paragraph-frame': () => {
      if (complexity <= 2) {
        return `
          <div style="font-family: Arial, sans-serif; padding: 20px;">
            <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Paragraph Frame</h2>
            <div style="max-width: 600px; margin: 0 auto; border: 2px solid #6366F1; border-radius: 16px; padding: 25px;">
              <p style="margin-bottom: 20px; font-size: 16px; line-height: 2.5;">
                <strong style="color: #6366F1;">Topic Sentence:</strong> I am writing about 
                <span style="border-bottom: 2px solid #333; padding: 0 100px;"></span>.
              </p>
              <p style="margin-bottom: 20px; font-size: 16px; line-height: 2.5;">
                <strong style="color: #10B981;">Detail 1:</strong> First, 
                <span style="border-bottom: 2px solid #333; padding: 0 150px;"></span>.
              </p>
              <p style="margin-bottom: 20px; font-size: 16px; line-height: 2.5;">
                <strong style="color: #F59E0B;">Detail 2:</strong> Next, 
                <span style="border-bottom: 2px solid #333; padding: 0 150px;"></span>.
              </p>
              <p style="font-size: 16px; line-height: 2.5;">
                <strong style="color: #8B5CF6;">Closing:</strong> In conclusion, 
                <span style="border-bottom: 2px solid #333; padding: 0 100px;"></span>.
              </p>
            </div>
            <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
          </div>
        `;
      }
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Paragraph Frame</h2>
          <div style="max-width: 650px; margin: 0 auto; border: 2px solid #6366F1; border-radius: 16px; padding: 25px;">
            <p style="margin-bottom: 15px; font-size: 14px; line-height: 2.2;">
              <strong style="color: #6366F1;">Topic Sentence:</strong> The main idea of this paragraph is 
              <span style="border-bottom: 1px solid #333; padding: 0 80px;"></span>.
            </p>
            <p style="margin-bottom: 15px; font-size: 14px; line-height: 2.2;">
              <strong style="color: #10B981;">Supporting Detail 1:</strong> First, 
              <span style="border-bottom: 1px solid #333; padding: 0 120px;"></span>.
            </p>
            <p style="margin-bottom: 15px; font-size: 14px; line-height: 2.2;">
              <strong style="color: #10B981;">Evidence/Example:</strong> For example, 
              <span style="border-bottom: 1px solid #333; padding: 0 120px;"></span>.
            </p>
            <p style="margin-bottom: 15px; font-size: 14px; line-height: 2.2;">
              <strong style="color: #F59E0B;">Supporting Detail 2:</strong> Additionally, 
              <span style="border-bottom: 1px solid #333; padding: 0 120px;"></span>.
            </p>
            <p style="margin-bottom: 15px; font-size: 14px; line-height: 2.2;">
              <strong style="color: #F59E0B;">Evidence/Example:</strong> This is shown by 
              <span style="border-bottom: 1px solid #333; padding: 0 120px;"></span>.
            </p>
            <p style="font-size: 14px; line-height: 2.2;">
              <strong style="color: #8B5CF6;">Conclusion:</strong> In summary, 
              <span style="border-bottom: 1px solid #333; padding: 0 100px;"></span>.
            </p>
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'essay-outline': () => {
      const bodyParagraphs = complexity >= 4 ? 4 : 3;
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">${bodyParagraphs + 2}-Paragraph Essay Outline</h2>
          <div style="max-width: 650px; margin: 0 auto;">
            <div style="border: 2px solid #6366F1; border-radius: 12px; padding: 15px; margin-bottom: 15px; background: #EEF2FF;">
              <p style="font-weight: bold; color: #6366F1; margin-bottom: 10px;">I. INTRODUCTION</p>
              <p style="margin-left: 20px; margin-bottom: 8px; font-size: 14px;">Hook: ________________________________</p>
              <p style="margin-left: 20px; margin-bottom: 8px; font-size: 14px;">Background: ________________________________</p>
              <p style="margin-left: 20px; font-size: 14px;">Thesis Statement: ________________________________</p>
            </div>
            ${Array.from({length: bodyParagraphs}, (_, i) => `
              <div style="border: 2px solid #10B981; border-radius: 12px; padding: 15px; margin-bottom: 15px; background: #ECFDF5;">
                <p style="font-weight: bold; color: #10B981; margin-bottom: 10px;">${['II', 'III', 'IV', 'V'][i]}. BODY PARAGRAPH ${i + 1}</p>
                <p style="margin-left: 20px; margin-bottom: 8px; font-size: 14px;">Topic Sentence: ________________________________</p>
                <p style="margin-left: 20px; margin-bottom: 8px; font-size: 14px;">Evidence: ________________________________</p>
                <p style="margin-left: 20px; margin-bottom: 8px; font-size: 14px;">Analysis: ________________________________</p>
                <p style="margin-left: 20px; font-size: 14px;">Transition: ________________________________</p>
              </div>
            `).join('')}
            <div style="border: 2px solid #8B5CF6; border-radius: 12px; padding: 15px; background: #F5F3FF;">
              <p style="font-weight: bold; color: #8B5CF6; margin-bottom: 10px;">${['III', 'IV', 'V', 'VI'][bodyParagraphs - 1]}. CONCLUSION</p>
              <p style="margin-left: 20px; margin-bottom: 8px; font-size: 14px;">Restate Thesis: ________________________________</p>
              <p style="margin-left: 20px; margin-bottom: 8px; font-size: 14px;">Summary of Points: ________________________________</p>
              <p style="margin-left: 20px; font-size: 14px;">Final Thought: ________________________________</p>
            </div>
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'word-bank-template': () => {
      const boxes = complexity <= 1 ? 6 : complexity <= 2 ? 9 : 12;
      const cols = complexity <= 1 ? 2 : 3;
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 10px;">Word Bank</h2>
          <p style="text-align: center; margin-bottom: 20px;">Topic: <span style="border-bottom: 2px solid #333; padding: 0 80px;"></span></p>
          <div style="display: grid; grid-template-columns: repeat(${cols}, 1fr); gap: 15px; max-width: 550px; margin: 0 auto;">
            ${Array.from({length: boxes}, () => `
              <div style="border: 2px solid #6366F1; border-radius: 12px; padding: 15px; text-align: center; min-height: ${complexity <= 1 ? '60px' : '50px'}; background: #EEF2FF;">
              </div>
            `).join('')}
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'editing-checklist': () => {
      const items = complexity >= 4 
        ? [
            { cat: 'Content', items: ['Thesis is clear and arguable', 'Each paragraph has a topic sentence', 'Evidence supports claims', 'Counterarguments addressed'] },
            { cat: 'Organization', items: ['Introduction hooks reader', 'Logical paragraph order', 'Transitions connect ideas', 'Conclusion synthesizes'] },
            { cat: 'Style', items: ['Varied sentence structure', 'Active voice used', 'Formal academic tone', 'Word choice precise'] },
            { cat: 'Mechanics', items: ['Spelling checked', 'Grammar correct', 'Punctuation accurate', 'Citations formatted'] }
          ]
        : [
            { cat: 'Content', items: ['Topic sentence present', 'Details support main idea', 'Makes sense'] },
            { cat: 'Organization', items: ['Has a beginning', 'Has a middle', 'Has an ending'] },
            { cat: 'Mechanics', items: ['Capital letters', 'Periods at end', 'Spelling checked', 'Neat handwriting'] }
          ];
      
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Editing Checklist</h2>
          <div style="max-width: 600px; margin: 0 auto;">
            ${items.map(section => `
              <div style="margin-bottom: 20px;">
                <h3 style="color: #6366F1; border-bottom: 2px solid #6366F1; padding-bottom: 5px; margin-bottom: 10px;">${section.cat}</h3>
                ${section.items.map(item => `
                  <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">
                    <div style="width: 20px; height: 20px; border: 2px solid #333; border-radius: 4px;"></div>
                    <span style="font-size: 14px;">${item}</span>
                  </div>
                `).join('')}
              </div>
            `).join('')}
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'task-checklist': () => {
      const steps = complexity <= 1 ? 5 : complexity <= 2 ? 6 : 8;
      return `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align: center; color: #4A7C59; margin-bottom: 10px;">Task Checklist</h2>
          <p style="text-align: center; margin-bottom: 20px;">Task: <span style="border-bottom: 2px solid #333; padding: 0 80px;"></span></p>
          <div style="max-width: 500px; margin: 0 auto;">
            ${Array.from({length: steps}, (_, i) => `
              <div style="display: flex; align-items: center; gap: 15px; padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
                <div style="width: 28px; height: 28px; border: 2px solid #4A7C59; border-radius: 6px;"></div>
                <div style="flex: 1; border-bottom: 1px dashed #ccc; padding-bottom: 5px;">Step ${i + 1}:</div>
              </div>
            `).join('')}
          </div>
          <div style="max-width: 500px; margin: 30px auto 0; padding: 15px; background: #ecfdf5; border-radius: 12px; text-align: center;">
            <p style="color: #10B981; font-weight: bold; margin: 0;">🌟 Great job completing all steps! 🌟</p>
          </div>
          <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Date: _________________</p>
        </div>
      `;
    },
    
    'homework-planner': () => `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align: center; color: #4A7C59; margin-bottom: 10px;">Weekly Homework Planner</h2>
        <p style="text-align: center; margin-bottom: 20px;">Week of: <span style="border-bottom: 2px solid #333; padding: 0 60px;"></span></p>
        <table style="border-collapse: collapse; width: 100%; max-width: 700px; margin: 0 auto;">
          <tr style="background: #4A7C59; color: white;">
            <th style="border: 2px solid #4A7C59; padding: 10px; width: 15%;">Day</th>
            <th style="border: 2px solid #4A7C59; padding: 10px;">Assignment</th>
            <th style="border: 2px solid #4A7C59; padding: 10px; width: 15%;">Due</th>
            <th style="border: 2px solid #4A7C59; padding: 10px; width: 10%;">Done</th>
          </tr>
          ${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => `
            <tr>
              <td style="border: 1px solid #ccc; padding: 12px; font-weight: bold; background: #f9fafb;">${day}</td>
              <td style="border: 1px solid #ccc; padding: 12px;"></td>
              <td style="border: 1px solid #ccc; padding: 12px;"></td>
              <td style="border: 1px solid #ccc; padding: 12px; text-align: center;">☐</td>
            </tr>
          `).join('')}
        </table>
        <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Parent Signature: _________________</p>
      </div>
    `,
    
    'timer-visual': () => `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Visual Timer Cards</h2>
        <p style="text-align: center; color: #666; margin-bottom: 20px;">Cut out cards. Show student how much time remains.</p>
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; max-width: 500px; margin: 0 auto;">
          ${[10, 5, 3, 2, 1, 0].map(mins => `
            <div style="border: 3px solid ${mins === 0 ? '#EF4444' : mins <= 2 ? '#F59E0B' : '#10B981'}; border-radius: 16px; padding: 20px; text-align: center; background: ${mins === 0 ? '#FEE2E2' : mins <= 2 ? '#FEF3C7' : '#ECFDF5'};">
              <div style="font-size: 36px; font-weight: bold; color: ${mins === 0 ? '#EF4444' : mins <= 2 ? '#F59E0B' : '#10B981'};">${mins}</div>
              <div style="font-size: 14px; color: #666;">${mins === 1 ? 'minute' : 'minutes'}</div>
              ${mins === 0 ? '<div style="font-size: 12px; color: #EF4444; margin-top: 5px;">Time\'s up!</div>' : ''}
            </div>
          `).join('')}
        </div>
        <p style="text-align: center; margin-top: 30px; font-size: 12px; color: #666;">For classroom use</p>
      </div>
    `,
    
    'transition-cards': () => `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align: center; color: #4A7C59; margin-bottom: 20px;">Transition Cards</h2>
        <p style="text-align: center; color: #666; margin-bottom: 20px;">Cut out cards. Use to signal activity changes.</p>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; max-width: 500px; margin: 0 auto;">
          ${[
            { text: '5 more minutes', emoji: '⏰', color: '#F59E0B' },
            { text: 'Time to clean up', emoji: '🧹', color: '#6366F1' },
            { text: 'Line up', emoji: '🚶', color: '#10B981' },
            { text: 'Sit down', emoji: '🪑', color: '#8B5CF6' },
            { text: 'Listen', emoji: '👂', color: '#EC4899' },
            { text: 'Get materials', emoji: '📚', color: '#06B6D4' }
          ].map(card => `
            <div style="border: 3px dashed ${card.color}; border-radius: 16px; padding: 20px; text-align: center;">
              <div style="font-size: 40px; margin-bottom: 10px;">${card.emoji}</div>
              <p style="font-weight: bold; color: #333; font-size: 16px;">${card.text}</p>
            </div>
          `).join('')}
        </div>
        <p style="text-align: center; margin-top: 30px; font-size: 12px; color: #666;">For classroom use</p>
      </div>
    `,
    
    'goal-tracker': () => `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align: center; color: #4A7C59; margin-bottom: 10px;">Goal Tracker</h2>
        <p style="text-align: center; margin-bottom: 20px;">My Goal: <span style="border-bottom: 2px solid #333; padding: 0 150px;"></span></p>
        <div style="max-width: 650px; margin: 0 auto;">
          <table style="border-collapse: collapse; width: 100%;">
            <tr style="background: #4A7C59; color: white;">
              <th style="border: 2px solid #4A7C59; padding: 10px;">Date</th>
              <th style="border: 2px solid #4A7C59; padding: 10px;">What I Did</th>
              <th style="border: 2px solid #4A7C59; padding: 10px; width: 100px;">Progress</th>
            </tr>
            ${Array.from({length: 7}, () => `
              <tr>
                <td style="border: 1px solid #ccc; padding: 12px; width: 100px;"></td>
                <td style="border: 1px solid #ccc; padding: 12px;"></td>
                <td style="border: 1px solid #ccc; padding: 12px;">
                  <div style="display: flex; gap: 4px; justify-content: center;">
                    ${Array.from({length: 5}, (_, i) => `<div style="width: 20px; height: 20px; border: 2px solid #6366F1; border-radius: 50%;"></div>`).join('')}
                  </div>
                </td>
              </tr>
            `).join('')}
          </table>
          <p style="margin-top: 15px; font-size: 12px; color: #666;">Progress: Fill in circles (1-5) to show how well you did.</p>
        </div>
        <p style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">Name: _________________ Target Date: _________________</p>
      </div>
    `,
  };

  return toolGenerators[toolId] ? toolGenerators[toolId]() : `
    <div style="font-family: Arial, sans-serif; padding: 40px; text-align: center;">
      <h2 style="color: #4A7C59; margin-bottom: 20px;">${toolId}</h2>
      <p style="color: #666;">Template coming soon.</p>
    </div>
  `;
};

const SupportToolkit = ({ user }) => {
  const navigate = useNavigate();
  const printRef = useRef(null);
  
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [generatingTool, setGeneratingTool] = useState(null);
  const [generatedContent, setGeneratedContent] = useState(null);

  // Filter tools based on grade level
  const getFilteredTools = () => {
    const filtered = {};
    
    Object.entries(TOOL_CATEGORIES).forEach(([catId, category]) => {
      if (selectedCategory !== 'all' && selectedCategory !== catId) return;
      
      const filteredTools = category.tools.filter(tool => 
        !selectedGrade || selectedGrade === 'all-grades' || tool.levels.includes(selectedGrade)
      );
      
      if (filteredTools.length > 0) {
        filtered[catId] = {
          ...category,
          tools: filteredTools
        };
      }
    });
    
    return filtered;
  };

  const handleGenerateTool = async (toolId, toolName) => {
    setGeneratingTool(toolId);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const gradeToUse = selectedGrade && selectedGrade !== 'all-grades' ? selectedGrade : 'early-elementary';
    const html = generateToolHTML(toolId, gradeToUse);
    setGeneratedContent({ toolId, toolName, html, gradeLevel: gradeToUse });
    setGeneratingTool(null);
    
    const gradeName = GRADE_LEVELS.find(g => g.id === gradeToUse)?.name || 'Default';
    toast.success(`${toolName} generated for ${gradeName}!`);
  };

  const handlePrint = () => {
    if (!generatedContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${generatedContent.toolName} - SpEdKit</title>
        <style>
          @media print {
            body { margin: 0; padding: 20px; }
          }
        </style>
      </head>
      <body>
        ${generatedContent.html}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  const handleDownloadDocx = () => {
    if (!generatedContent) return;
    
    // Create a simple DOCX-compatible HTML file
    const docContent = `
      <!DOCTYPE html>
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
      <head>
        <meta charset="utf-8">
        <title>${generatedContent.toolName}</title>
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
          </w:WordDocument>
        </xml>
        <![endif]-->
      </head>
      <body>
        ${generatedContent.html}
        <p style="margin-top: 30px; font-size: 10px; color: #999;">Generated by SpEdKit Support Staff Toolkit - Editable in Microsoft Word</p>
      </body>
      </html>
    `;
    
    const blob = new Blob([docContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${generatedContent.toolName.replace(/\s+/g, '_')}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Downloaded as editable Word document!');
  };

  const filteredTools = getFilteredTools();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="text-text-muted hover:text-primary">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <SpEdKitLogo size="sm" />
            </div>
            {generatedContent && (
              <div className="flex gap-2">
                <Button
                  onClick={handleDownloadDocx}
                  variant="outline"
                  className="rounded-full"
                  data-testid="download-docx-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download Word
                </Button>
                <Button
                  onClick={handlePrint}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full"
                  data-testid="print-tool-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print PDF
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-6 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
              <Wrench className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-text-primary">
                Support Staff Toolkit
              </h1>
              <p className="text-text-secondary text-sm">
                Generate grade-appropriate accommodation tools
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-text-muted mb-2 block">Grade Level (adjusts complexity)</Label>
              <Select value={selectedGrade} onValueChange={setSelectedGrade}>
                <SelectTrigger data-testid="grade-select">
                  <SelectValue placeholder="Select Grade Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-grades">All Grade Levels</SelectItem>
                  {GRADE_LEVELS.map(level => (
                    <SelectItem key={level.id} value={level.id}>
                      {level.name} ({level.grades})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-text-muted mb-2 block">Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(TOOL_CATEGORIES).map(([id, cat]) => (
                    <SelectItem key={id} value={id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {selectedGrade && selectedGrade !== 'all-grades' && (
            <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
              <span>💡</span>
              Tools will be generated with complexity appropriate for {GRADE_LEVELS.find(g => g.id === selectedGrade)?.name}
            </p>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Tools List */}
          <div className="space-y-6">
            {Object.entries(filteredTools).map(([catId, category]) => {
              const Icon = category.icon;
              return (
                <div key={catId} className="bg-white rounded-xl border overflow-hidden">
                  <div className="p-4 border-b bg-gray-50 flex items-center gap-3">
                    <Icon className="w-5 h-5 text-primary" />
                    <h2 className="font-heading font-semibold text-text-primary">{category.name}</h2>
                    <Badge variant="outline" className="ml-auto">{category.tools.length}</Badge>
                  </div>
                  <div className="divide-y">
                    {category.tools.map(tool => (
                      <div
                        key={tool.id}
                        className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-text-primary text-sm">{tool.name}</p>
                          <p className="text-xs text-text-muted">{tool.description}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleGenerateTool(tool.id, tool.name)}
                          disabled={generatingTool === tool.id}
                          className="rounded-full bg-orange-500 hover:bg-orange-600 text-white"
                          data-testid={`generate-${tool.id}-btn`}
                        >
                          {generatingTool === tool.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-1" />
                              Generate
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {Object.keys(filteredTools).length === 0 && (
              <div className="bg-white rounded-xl border p-8 text-center">
                <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-text-muted">No tools available for the selected filters.</p>
                <p className="text-sm text-text-muted mt-1">Try selecting a different grade level or category.</p>
              </div>
            )}
          </div>

          {/* Preview Panel */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                <h2 className="font-heading font-semibold text-text-primary">Preview</h2>
                {generatedContent && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleDownloadDocx}
                      className="rounded-full"
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      Word
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handlePrint}
                      className="rounded-full"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                )}
              </div>
              <div 
                ref={printRef}
                className="p-4 min-h-[400px] bg-white overflow-auto"
                style={{ maxHeight: 'calc(100vh - 300px)' }}
              >
                {generatedContent ? (
                  <div>
                    <div 
                      dangerouslySetInnerHTML={{ __html: generatedContent.html }}
                      className="preview-content"
                    />
                    <p className="text-xs text-center text-gray-400 mt-4 border-t pt-4">
                      Generated for: {GRADE_LEVELS.find(g => g.id === generatedContent.gradeLevel)?.name || 'Default'}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                      <Wrench className="w-8 h-8 text-orange-400" />
                    </div>
                    <p className="text-text-muted">Select a tool to generate</p>
                    <p className="text-sm text-text-muted mt-1">
                      Choose a grade level for appropriate complexity
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SupportToolkit;
